import { fetchAllPillarsCached } from '../api/pillars';
import { useState, useEffect, useCallback, useRef, startTransition as reactStartTransition } from 'react';
import { apiClient } from '../api/client';
import { getSocket } from '../utils/socketManager';
import { getSocketBaseUrl } from '../utils/socketUtils';
// import { useDataSocket } from '../context/DataSocketContext'; // Removed DataSocket import
import isEqual from 'lodash/isEqual';

const safeStartTransition = typeof reactStartTransition === 'function'
  ? reactStartTransition
  : (cb) => cb();

const API_URL = '';  // Use relative URLs

// CRITICAL: Sanitization function to prevent React component contamination
const sanitizeKpiData = (data, index = 0) => {
  try {
    // Multiple layers of validation
    if (!data || typeof data !== 'object') return null;
    if (data.$$typeof || data._owner || data.type || data.props || data.ref) return null; // React component/element
    
    // Handle arrays
    if (Array.isArray(data)) {
      return data
        .map((item, idx) => sanitizeKpiData(item, idx))
        .filter(Boolean); // Remove any nulls
    }
    
    // Create completely new object with only safe primitive/plain object properties
    const sanitized = {};
    
    for (const [key, value] of Object.entries(data)) {
      // Skip React internal properties
      if (key.startsWith('$$') || key === '_owner' || key === 'type' || key === 'props' || key === 'ref') {
        continue;
      }
      
      if (value === null || value === undefined) {
        sanitized[key] = value;
      } else if (Array.isArray(value)) {
        sanitized[key] = sanitizeKpiData(value);
      } else if (typeof value === 'object') {
        sanitized[key] = sanitizeKpiData(value);
      } else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        sanitized[key] = value;
      }
      // Skip functions or other complex types
    }
    
    return sanitized;
  } catch (error) {
    console.error('🚨 [SANITIZE] Error sanitizing KPI data:', error, data);
    return null;
  }
};

// Sanitization function specifically for pillar data from API
const sanitizePillarData = (pillar, index = 0) => {
  try {
    // Multiple layers of validation
    if (!pillar || typeof pillar !== 'object') return null;
    if (pillar.$$typeof || pillar._owner || pillar.type || pillar.props || pillar.ref) return null; // React component/element
    
    // Create completely new object with only the needed properties
    const sanitized = {
      id: pillar.id,
      name: pillar.name,
      key: pillar.key,
      framework_id: pillar.framework_id,
      parent_pillar_id: pillar.parent_pillar_id,
      is_visible: pillar.is_visible,
      order_index: pillar.order_index,
      analysis_behavior_flag: pillar.analysis_behavior_flag,
      color: pillar.color
    };
    
    // Handle sub-pillars if they exist
    if (Array.isArray(pillar.subPillars)) {
      sanitized.subPillars = pillar.subPillars
        .map((subPillar, subIndex) => sanitizePillarData(subPillar, subIndex))
        .filter(Boolean);
    }
    
    return sanitized;
  } catch (error) {
    console.error('🚨 [SANITIZE] Error sanitizing pillar data:', error, pillar);
    return null;
  }
};

export const useKPIData = (selectedView, organizationSettings = null) => {
  // State management
  const [kpiData, setKPIData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Track when organization settings change
  useEffect(() => {
    // Only log if there are no pillars (potential issue)
    if (organizationSettings && !organizationSettings.pillars) {
      console.warn('⚠️ [useKPIData] Organization settings received but no pillars defined');
    }
  }, [organizationSettings]);
  
  // Context and refs - REMOVED DataSocket related refs
  // const { subscribe, refreshData } = useDataSocket(); 
  const prevViewRef = useRef(selectedView?.id);
  const lastInitialFetchKeyRef = useRef('');
  // const previousDataRef = useRef(null); // This ref was unused, removing
  const fetchInProgressRef = useRef(false);
  const renderCountRef = useRef(0); // For debugging
  const socketRef = useRef(null);

  // Get organization pillar settings - now accepts current settings to avoid stale closure
  const getOrganizationPillarSettings = (currentOrgSettings = organizationSettings) => {
    // If organization settings are provided, return the pillar keys as an array
    if (currentOrgSettings?.pillars) {
      return currentOrgSettings.pillars; // These are pillar keys (strings)
    }
    
    // Fallback: try URL parameters for backward compatibility
    const params = new URLSearchParams(window.location.search);
    const orgPillars = params.get('orgPillars');
    const urlPillars = orgPillars ? orgPillars.split(',').map(id => parseInt(id, 10)) : [];
    
    return urlPillars;
  };

  // Increment render counter for debugging
  useEffect(() => {
    renderCountRef.current += 1;
  });

  // Function to fetch KPI data from the API
  const fetchKPIData = useCallback(async () => {
    if (!selectedView || !selectedView.id || fetchInProgressRef.current) return;
    
    fetchInProgressRef.current = true;
    setIsLoading(true);
    setError(null); // Reset error state at the beginning of a fetch
    
    try {
      // Step 1: Fetch parent KPIs (these are the items that *could* become KPI boxes)
      const kpisResponse = await fetch(`${API_URL}/api/views/${selectedView.id}/kpis`);
      if (!kpisResponse.ok) {
        throw new Error(`Error fetching KPI data: ${kpisResponse.statusText} (status: ${kpisResponse.status})`);
      }
      const rawParentKpiTemplates = await kpisResponse.json();
      
      // CRITICAL: Sanitize KPI data immediately to prevent React component contamination
      const parentKpiTemplates = sanitizeKpiData(rawParentKpiTemplates);
      if (!parentKpiTemplates) {
        throw new Error('KPI data sanitization failed - received contaminated data');
      }
      
      console.log('✅ [SANITIZE] KPI templates sanitized successfully:', {
        originalCount: Array.isArray(rawParentKpiTemplates) ? rawParentKpiTemplates.length : 'not-array',
        sanitizedCount: Array.isArray(parentKpiTemplates) ? parentKpiTemplates.length : 'not-array',
        sampleTemplate: parentKpiTemplates?.[0] || 'none'
      });

      // Step 2: Fetch all pillars for the view to get their true visibility and hierarchy
      const rawAllPillars = await fetchAllPillarsCached(selectedView.id, API_URL);
      
      // CRITICAL: Sanitize pillar data immediately to prevent React component contamination  
      const allPillars = rawAllPillars
        .map((pillar, index) => sanitizePillarData(pillar, index))
        .filter(Boolean); // Remove any nulls from sanitization
        
      console.log('✅ [SANITIZE] Pillar data sanitized successfully:', {
        originalCount: Array.isArray(rawAllPillars) ? rawAllPillars.length : 'not-array',
        sanitizedCount: allPillars.length,
        samplePillar: allPillars?.[0] || 'none'
      });

      // Step 3: Apply organization pillar filtering
      const organizationPillarSettings = getOrganizationPillarSettings();
      const hasOrgSettings = organizationSettings !== null;
      
      // Step 4: Process and filter parentKpiTemplates
      const processedKpiData = parentKpiTemplates.reduce((acc, kpiTemplate) => {
        const parentPillarId = kpiTemplate.pillarId || kpiTemplate.pillar_id;
        
        if (!parentPillarId) {
          console.warn("KPI Hook: KPI template missing pillarId", kpiTemplate);
          return acc;
        }

        const fullParentPillarData = allPillars.find(p => p.id === parentPillarId && (p.parent_pillar_id === null || p.parent_pillar_id === undefined));
        
        if (!fullParentPillarData) {
          console.warn(`KPI Hook: Could not find full data for parent pillar ID ${parentPillarId} in allPillars array. KPI Template:`, kpiTemplate);
          return acc; 
        }

        // Check organization pillar settings - if set, only show enabled pillars
        // CRITICAL: If organization settings exist, we MUST respect them, even if the array is empty (0 pillars)
        if (hasOrgSettings) {
          // Handle both pillar IDs (numbers) and keys (strings) for compatibility
          // Priority is now on numeric pillar_id as it is unique
          const isIncludedById = organizationPillarSettings.includes(parentPillarId) || 
                                 organizationPillarSettings.includes(parentPillarId.toString()) ||
                                 organizationPillarSettings.includes(parseInt(parentPillarId));
          
          const isIncludedByKey = organizationPillarSettings.includes(fullParentPillarData.key);
          
          // Legacy support for id_framework_id combinations
          const isIncludedByIdFramework = fullParentPillarData.framework_id && 
            organizationPillarSettings.includes(`${parentPillarId}_${fullParentPillarData.framework_id}`);
          
          const isIncluded = isIncludedById || isIncludedByKey || isIncludedByIdFramework;
          
          if (!isIncluded) {
            // Skip this pillar if not enabled by organization
            return acc; 
          }
        }

        /* 
         * KPI BOX VISIBILITY LOGIC (FOLLOWS SAME PRINCIPLE AS ANALYSIS LOGIC):
         * 
         * This determines whether to show a KPI box for a parent pillar based on
         * the ORIGINAL PILLAR VISIBILITY DATA (is_visible field).
         * 
         * DECISION TREE:
         * 1. If parent.is_visible === true  → Show KPI box (parent has data)
         * 2. If parent.is_visible === false → Only show KPI box if it has visible children
         *    (parent is just a grouping mechanism for child pillars)
         * 
         * This ensures that:
         * - Parents with their own data always get a KPI box
         * - Parents that are just folders only get a KPI box if they contain visible children
         * - Clicking the box will follow the analysis logic (parent data vs first child data)
         */
        const isParentItselfVisible = fullParentPillarData.is_visible === true;
        let shouldShowPillarBox = isParentItselfVisible;

        if (!isParentItselfVisible) {
          // Parent is hidden - check if it has visible children to justify showing the box
          const hasVisibleChildren = allPillars.some(subPillar => 
            subPillar.parent_pillar_id === parentPillarId && subPillar.is_visible === true
          );
          if (hasVisibleChildren) {
            shouldShowPillarBox = true; // Show box as a gateway to child pillar data
          }
        }
        
        if (shouldShowPillarBox) {
          acc.push({
            ...kpiTemplate, // Spread the original KPI template data (name, value, etc.)
            pillarId: parentPillarId, // Ensure pillarId is consistent
            // IMPORTANT: `is_visible` here MUST be the parent's own true visibility status from allPillars
            is_visible: fullParentPillarData.is_visible 
          });
        }
        return acc;
      }, []);
      
      // FINAL SAFETY: Sanitize the processed KPI data one more time before setting to state
      const finalSanitizedKpiData = sanitizeKpiData(processedKpiData);
      if (!finalSanitizedKpiData) {
        throw new Error('Final KPI data sanitization failed - processed data is contaminated');
      }
      
      console.log('✅ [FINAL SANITIZE] Processed KPI data sanitized before state update:', {
        processedCount: Array.isArray(processedKpiData) ? processedKpiData.length : 'not-array',
        finalSanitizedCount: Array.isArray(finalSanitizedKpiData) ? finalSanitizedKpiData.length : 'not-array'
      });
      
      safeStartTransition(() => {
        setKPIData(prevData => {
          // Basic check for stability if data is identical to prevent re-renders
          if (isEqual(prevData, finalSanitizedKpiData)) {
            return prevData;
          }
          return finalSanitizedKpiData;
        });
        // setError(null); // Already set to null at the start
        setIsLoading(false);
      });
    } catch (err) {
      console.error("Error in fetchKPIData:", err); // Log the actual error object
      safeStartTransition(() => {
        setError(err.message);
        setKPIData([]); 
        setIsLoading(false);
      });
    } finally {
      fetchInProgressRef.current = false;
    }
  }, [selectedView, organizationSettings]); // Dependencies: view and organization settings

  // Initial fetch of KPI data when view changes
  useEffect(() => {
    setIsLoading(true);
    
    if (!selectedView) {
      return;
    }
    
    const viewChanged = prevViewRef.current !== selectedView.id;
    const dataEmpty = kpiData.length === 0;
    const fetchKey = `${selectedView.id}:${dataEmpty ? 'empty' : 'has-data'}`;

    if (viewChanged) {
      lastInitialFetchKeyRef.current = '';
    }
    
    if ((viewChanged || dataEmpty) && lastInitialFetchKeyRef.current !== fetchKey) {
      prevViewRef.current = selectedView.id;
      lastInitialFetchKeyRef.current = fetchKey;
      console.log('🔄 [useKPIData] Fetching KPI data due to view change or empty data');
      fetchKPIData();
    }
  }, [selectedView, fetchKPIData, kpiData.length]);

  // Track pillar count to prevent unnecessary re-fetches when settings object changes but pillar count stays the same
  const pillarCountRef = useRef(0);
  
  // Separate useEffect for organization settings changes
  useEffect(() => {
    if (organizationSettings && selectedView) {
      const newPillarCount = organizationSettings.pillars?.length || 0;
      const pillarCountChanged = pillarCountRef.current !== newPillarCount;
      
      if (pillarCountChanged) {
        console.log(`🔄 [useKPIData] Pillar count changed: ${pillarCountRef.current} → ${newPillarCount}`);
        pillarCountRef.current = newPillarCount;
        fetchKPIData();
      } else {
        // Skip logging when pillar count is unchanged to reduce noise
      }
    }
  }, [organizationSettings, fetchKPIData, selectedView]);

  // Subscribe to indicator WebSocket updates (separate effect)
  useEffect(() => {
    // Track handlers for cleanup
    let activeSocket = null;
    let handleConnect = null;
    let handleIndicatorsUpdate = null;
    let handleDataUpdate = null;

    // Clear any existing socket reference immediately on dependency change
    if (socketRef.current) {
      socketRef.current = null;
    }

    // Only connect when a view is selected
    if (!selectedView?.id) return;

    const baseUrl = getSocketBaseUrl();
    if (!baseUrl) return;

    // Debounce connection to prevent rapid churn
    const timeoutId = setTimeout(() => {
      // Use shared socket manager
      const socket = getSocket('/indicators-updates');
      if (!socket) return;

      activeSocket = socket;
      socketRef.current = socket;

      // Ensure socket is connected
      if (socket.disconnected) {
        socket.connect();
      }

      handleIndicatorsUpdate = (payload) => {
        // CRITICAL: Reject empty payloads that could trigger unnecessary refreshes
        if (!payload) {
          console.log('[useKPIData] Skipping indicators_update - no payload');
          return;
        }
        
        try {
          const payloadViewId = payload?.view_id || payload?.viewId || payload?.data?.view_id || payload?.data?.viewId;
          // Must have a view_id to be valid
          if (payloadViewId === undefined) {
            console.log('[useKPIData] Skipping indicators_update - missing view_id');
            return;
          }
          if (Number(payloadViewId) !== Number(selectedView.id)) {
            return;
          }
        } catch (_) {
          return;
        }
        console.log('[useKPIData] Refreshing KPI data from WebSocket update');
        fetchKPIData();
      };

      handleDataUpdate = (data) => {
        if (!data) return;
        if (data.type === 'indicators') {
          // Must have valid filter info to trigger refresh
          const metaView = data._meta?.filters?.view_id || data._meta?.filters?.view || data._meta?.filters?.view_code;
          // If metaView exists, it must match our view
          if (metaView !== undefined && String(metaView) !== String(selectedView.id) && String(metaView) !== String(selectedView.iso3)) {
            return;
          }
          // If no metaView filter, check the data itself
          if (!metaView && data.data) {
            const dataView = data.data?.view_id || data.data?.viewId;
            if (dataView !== undefined && Number(dataView) !== Number(selectedView.id)) {
              return;
            }
          }
          console.log('[useKPIData] Refreshing KPI data from data_update');
          fetchKPIData();
        }
      };

      handleConnect = () => {
        // Request server to send latest indicators for this view (best effort)
        socket.emit('request_data', { view_id: selectedView.id });
      };

      if (socket.connected) {
        handleConnect();
      }

      socket.on('connect', handleConnect);
      socket.on('indicators_update', handleIndicatorsUpdate);
      socket.on('data_update', handleDataUpdate);
    }, 500); // 500ms debounce

    return () => {
      clearTimeout(timeoutId);
      if (activeSocket) {
        if (handleConnect) activeSocket.off('connect', handleConnect);
        if (handleIndicatorsUpdate) activeSocket.off('indicators_update', handleIndicatorsUpdate);
        if (handleDataUpdate) activeSocket.off('data_update', handleDataUpdate);
      }
      socketRef.current = null;
    };
  }, [selectedView?.id, selectedView?.iso3, fetchKPIData]);

  // Function to manually refresh KPI data - MODIFIED
  const refreshKPIData = useCallback(() => {
    fetchKPIData();
    // Also request a refresh of indicators data from the server - REMOVED WebSocket part
    // if (selectedView?.id) {
    //   refreshData('indicators', { view_id: selectedView.id });
    // }
  }, [fetchKPIData]); // Removed refreshData and selectedView from dependencies as they are no longer used here

  return { kpiData, isLoading, error, refreshKPIData };
};
